import React, { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("sac_token");
    if (!token) { setLoading(false); return; }
    // /admin/me returns role + permissions; falls back to /auth/whoami if older backend.
    api.get("/admin/me")
      .then((r) => setUser(r.data))
      .catch(() => api.get("/auth/whoami").then((r) => setUser(r.data)).catch(() => setUser(null)))
      .finally(() => setLoading(false));
  }, []);

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/admin/me");
      setUser(data);
      return data;
    } catch { return null; }
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // If the account has 2FA enabled, the server returns a `mfa_required`
    // payload with a short-lived challenge_token instead of a full JWT.
    // We bubble that up so the Login page can show the 6-digit-code prompt.
    if (data?.mfa_required) {
      return { mfa_required: true, challenge_token: data.challenge_token, user: data.user };
    }
    localStorage.setItem("sac_token", data.token);
    try {
      const me = await api.get("/admin/me");
      setUser(me.data);
      return me.data;
    } catch {
      setUser(data.user);
      return data.user;
    }
  };

  /**
   * Second leg of the login dance — exchange the challenge token + the
   * 6-digit TOTP code for a real JWT. Mirrors the shape of `login()` so
   * the Login page can reuse the same "store + fetch profile" tail.
   */
  const verifyMfa = async (challenge_token, code) => {
    const { data } = await api.post("/auth/2fa/verify", { challenge_token, code });
    localStorage.setItem("sac_token", data.token);
    try {
      const me = await api.get("/admin/me");
      setUser(me.data);
      return me.data;
    } catch {
      setUser(data.user);
      return data.user;
    }
  };

  const logout = () => {
    localStorage.removeItem("sac_token");
    localStorage.removeItem("sac_owner_token");
    setUser(null);
    window.location.href = "/admin/login";
  };

  /**
   * Owner-only: swap the current token for a short-lived impersonation JWT
   * that the API treats as the target user. We stash the original owner
   * token under `sac_owner_token` so a single click can restore it.
   */
  const impersonate = async (userId) => {
    const { data } = await api.post(`/admin/users/${userId}/impersonate`);
    const currentToken = localStorage.getItem("sac_token");
    if (currentToken) localStorage.setItem("sac_owner_token", currentToken);
    localStorage.setItem("sac_token", data.token);
    const me = await api.get("/admin/me");
    setUser(me.data);
    return me.data;
  };

  /** Restore the owner token stashed by `impersonate()`. */
  const endImpersonation = async () => {
    const ownerToken = localStorage.getItem("sac_owner_token");
    if (!ownerToken) {
      // No stash — safest fallback is a full sign-out so the owner can
      // re-authenticate cleanly.
      logout();
      return null;
    }
    localStorage.setItem("sac_token", ownerToken);
    localStorage.removeItem("sac_owner_token");
    const me = await api.get("/admin/me");
    setUser(me.data);
    return me.data;
  };

  const can = (perm) => {
    if (!user) return false;
    if (user.role === "owner") return true;
    return (user.permissions || []).includes(perm);
  };

  return <AuthContext.Provider value={{ user, login, verifyMfa, logout, loading, can, refreshUser, impersonate, endImpersonation }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
